import classNames from 'classnames';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import type { SimpleReward } from '@sorare/core/src/lib/rewards';
import { unitMapping } from '@sorare/core/src/lib/style';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { RewardOverviewImage } from './RewardOverviewImage';
import { RewardOverviewTitle } from './RewardOverviewTitle';
import { Variant } from './types';

const DEFAULT_COLOR = '173, 173, 173';

const rgbColorPerRarity = {
  [Rarity.limited]: '255, 184, 0',
  [Rarity.rare]: '191, 3, 14',
  [Rarity.super_rare]: '3, 42, 185',
  [Rarity.unique]: '80, 41, 107',
};

const Root = styled.div`
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  position: relative;

  &.classic {
    --text-height: var(--lh-14);
  }

  &.imageOnly {
    --text-height: 0;
  }

  &.classic,
  &.imageOnly {
    display: flex;
    flex-direction: column;
    align-items: center;
    aspect-ratio: unset;
    width: unset;
    height: calc(var(--size) + var(--text-height) / 2);
  }
`;
const Content = styled(Horizontal)`
  width: 100%;
  height: 100%;
  border-radius: var(--unit);

  --border-size: 1px;
  --border-gradient: linear-gradient(
    rgb(var(--color)),
    rgba(var(--color), 0.2)
  );
  ${borderGradientStyle}

  background:
    radial-gradient(
        circle at 50%,
        rgba(var(--color), 0.3),
        rgba(var(--color), 0) 60%
      ), var(--c-nl-400);

  .classic &,
  .imageOnly & {
    width: var(--size);
    height: var(--size);
  }
`;
const ImageContainer = styled(Horizontal).attrs({ center: true })`
  height: 70%;
  aspect-ratio: 1/1;
  margin: auto;
`;
export const RewardOverviewChip = styled(Horizontal).attrs({ gap: 1 })`
  width: max-content;
  height: var(--quadruple-unit);
  gap: var(--unit);
  padding: 0 var(--unit);

  border-radius: var(--unit);
  border: 0.5px solid transparent;
  background:
    radial-gradient(
        circle var(--double-unit) at var(--double-unit) 50%,
        rgba(var(--color), 0.3),
        rgba(var(--color), 0)
      )
      padding-box,
    linear-gradient(#262626, #262626) padding-box,
    linear-gradient(rgb(var(--color)), rgba(var(--color), 0.2)) border-box;
  background-repeat: no-repeat;
`;
const TextContainer = styled.div`
  position: relative;
  margin-top: calc(-1 * var(--text-height) / 2);
`;

type Props = {
  variant: Variant;
  simpleReward: SimpleReward;
  size?: keyof typeof unitMapping;
};

export const RewardOverview = ({ variant, simpleReward, size = 4 }: Props) => {
  if (!simpleReward.count) return null;

  const color =
    simpleReward.rarity && simpleReward.rarity in rgbColorPerRarity
      ? rgbColorPerRarity[simpleReward.rarity as keyof typeof rgbColorPerRarity]
      : DEFAULT_COLOR;

  if (variant === Variant.INLINE) {
    return (
      <RewardOverviewChip style={{ '--color': color } as CSSProperties}>
        <ImageContainer>
          <RewardOverviewImage simpleReward={simpleReward} />
        </ImageContainer>
        <LabelS as="p" bold>
          <RewardOverviewTitle variant={variant} simpleReward={simpleReward} />
        </LabelS>
      </RewardOverviewChip>
    );
  }

  return (
    <Root
      className={classNames(variant)}
      style={
        {
          '--color': color,
          '--size': unitMapping[size],
        } as CSSProperties
      }
    >
      <Content>
        <ImageContainer>
          <RewardOverviewImage simpleReward={simpleReward} />
        </ImageContainer>
      </Content>
      {variant !== Variant.IMAGE_ONLY && (
        <TextContainer>
          <LabelS as="p" bold className="text-center">
            <RewardOverviewTitle
              variant={variant}
              simpleReward={simpleReward}
            />
          </LabelS>
        </TextContainer>
      )}
    </Root>
  );
};
